import { all, put, takeEvery, fork } from "redux-saga/effects";
import { apiURL } from "../actions/helpers";
import axios from "axios";

// Action Types
export const LIVE_ALERTS_LOAD = "LIVE_ALERTS/LOAD";
export const LIVE_ALERTS_LOAD_SUCCESS = "LIVE_ALERTS/LOAD/SUCCESS";
export const LIVE_ALERTS_LOAD_FAILURE = "LIVE_ALERTS/LOAD/FAILURE";
export const LIVE_ALERTS_UPDATE_STOP_LOSS = "LIVE_ALERTS/UPDATE_STOP_LOSS";
export const LIVE_ALERTS_CLEAR_RECENT_UPDATE =
  "LIVE_ALERTS/CLEAR_RECENT_UPDATE";

// Action Creators
export const liveAlertsLoad = () => ({
  type: LIVE_ALERTS_LOAD,
});

export const liveAlertsLoadSuccess = (data) => ({
  type: LIVE_ALERTS_LOAD_SUCCESS,
  data,
});

export const liveAlertsLoadFailure = (error) => ({
  type: LIVE_ALERTS_LOAD_FAILURE,
  error,
});

// Update stop loss from WebSocket trailing stop loss update
export const liveAlertsUpdateStopLoss = (
  tradeId,
  oldStopLoss,
  newStopLoss,
) => ({
  type: LIVE_ALERTS_UPDATE_STOP_LOSS,
  tradeId,
  oldStopLoss,
  newStopLoss,
  timestamp: Date.now(),
});

// Clear the recent update highlight after timeout
export const liveAlertsClearRecentUpdate = (tradeId) => ({
  type: LIVE_ALERTS_CLEAR_RECENT_UPDATE,
  tradeId,
});

// Sagas
function* fetchLiveAlerts() {
  try {
    const response = yield axios.get(`${apiURL}/ai-alerts/live`, {
      withCredentials: true,
    });
    yield put(liveAlertsLoadSuccess(response.data?.alertGroups || []));
  } catch (error) {
    yield put(liveAlertsLoadFailure(error));
  }
}

function* listenLiveAlertsLoad() {
  yield takeEvery(LIVE_ALERTS_LOAD, fetchLiveAlerts);
}

// Root Saga
export function* saga() {
  yield all([fork(listenLiveAlertsLoad)]);
}

// Initial State
const INIT_STATE = {
  alertGroups: [],
  loading: false,
  error: null,
  // Map of tradeId -> { timestamp, oldValue, newValue } for recently updated stop losses
  recentlyUpdatedAlerts: {},
};

// Reducer
const reducer = (state = INIT_STATE, action) => {
  switch (action.type) {
    case LIVE_ALERTS_LOAD:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case LIVE_ALERTS_LOAD_SUCCESS:
      return {
        ...state,
        alertGroups: action.data,
        loading: false,
        error: null,
      };
    case LIVE_ALERTS_LOAD_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.error,
      };
    case LIVE_ALERTS_UPDATE_STOP_LOSS: {
      // Update the stopLoss value in alertGroups for the matching trade
      const updatedAlertGroups = state.alertGroups.map((group) => ({
        ...group,
        alerts: group.alerts.map((alert) =>
          alert.id === action.tradeId
            ? { ...alert, stopLoss: action.newStopLoss }
            : alert,
        ),
      }));

      // Add to recently updated alerts for highlight
      const updatedRecentlyUpdatedAlerts = {
        ...state.recentlyUpdatedAlerts,
        [action.tradeId]: {
          timestamp: action.timestamp,
          oldValue: action.oldStopLoss,
          newValue: action.newStopLoss,
        },
      };

      return {
        ...state,
        alertGroups: updatedAlertGroups,
        recentlyUpdatedAlerts: updatedRecentlyUpdatedAlerts,
      };
    }
    case LIVE_ALERTS_CLEAR_RECENT_UPDATE: {
      // Remove the tradeId from recently updated alerts
      const { [action.tradeId]: removed, ...remainingUpdates } =
        state.recentlyUpdatedAlerts;
      return {
        ...state,
        recentlyUpdatedAlerts: remainingUpdates,
      };
    }
    default:
      return state;
  }
};

// Selectors
export const getLiveAlertsData = (state) => state.liveAlerts.alertGroups;
export const getLiveAlertsLoading = (state) => state.liveAlerts.loading;
export const getRecentlyUpdatedAlerts = (state) =>
  state.liveAlerts.recentlyUpdatedAlerts;

export default reducer;
